import { writable, readable, derived } from 'utils/state'

const customRegionContact = (mail = 'contact@f-f-p.org', tel = '01 30 21 47 45') => `f-f-p.org\n${mail}\n${tel}`

const Store = {
  constants: readable({
    NBSP_SYMBOL: '␣',
    DECIMALS: 3,
    MIN_FONT_SIZE: 8,
    MAX_FONT_SIZE: 120,
    EXTENSION: '.ffp',
    BLEED: 5, // mm
    BROWSER_SUPPORT: {
      chrome: '>=84',
      firefox: '>=79'
    }
  }),

  app: {
    isModified: writable(true),
    showLanding: writable(true),
    hasOpenModal: writable(false),
    showGuides: writable(false),
    papersBeingRendered: writable(new Set()),
    isRendering: undefined, // See derived below
    isNotRendering: undefined, // See derived below
    isExporting: writable(false),
    isSaving: writable(false),

    helpMessages: readable([
      window.ENV.features.regions && [
        <p>Sélectionnez la région concernée par votre annonce pour préciser les informations de contact sur le document.</p>
      ],
      (window.ENV.features.h1 || window.ENV.features.h2 || window.ENV.features.h3) && [
        window.ENV.features.h1 && <p>Rédigez le titre (obligatoire) de votre annonce.</p>,
        (window.ENV.features.h2 && window.ENV.features.h3) && <p>Rédigez un sous-titre et un court paragraphe contenant des informations complémentaires.</p>,
        <p>Vous souhaitez générer des paysages sans textes ? Rendez-vous sur <a href='paysage/' target='_blank'>sur cette page</a>.</p>
      ],
      window.ENV.features.landscape && [
        <p>Générez un paysage à votre convenance.</p>,
        <p>Cliquez sur <i class='fa fa-magic' /> pour générer une nouvelle palette de couleurs, puis cliquez sur une couleur pour l’appliquer en arrière-plan.</p>,
        <p>Cliquez sur le paysage pour le re-générer sans modifier la palette de couleurs.</p>
      ],
      window.ENV.features.logos && [
        <p>
          Ajouter un ou plusieurs logos partenaires. Ils apparaîtront en bas des formats appropriés.<br />
          Les logos doivent être de préférence en noir sur blanc, au format <b>png</b>.
        </p>,
        <p>Cliquer sur <i class='fa fa-trash-o' /> pour supprimer un logo précédemment ajouté.</p>,
        <p>Glissez-déposez pour changer l’ordre des logos dans la liste.</p>
      ],
      window.ENV.features.cover && [
        <p>
          Sélectionner une image de couverture, au format <b>png</b> ou <b>jpg</b>.
        </p>,
      ],
      window.ENV.features.formats && [
        <p>Sélectionnez les formats que vous souhaitez exporter.</p>,
        <p>Pour créer un nouveau format, cliquez sur <span style='white-space:nowrap'>«&thinsp;<i class='fa fa-plus' /> Ajouter un format&thinsp;»</span> et entrez son nom et ses dimensions.</p>
      ],
      [
        <p>Vérifiez les différents formats avant l’export.</p>,
        <p>
          <b>Afin d’optimiser au mieux la lisibilité du texte sur le paysage :</b><br/>
          Pour chaque format, vous pouvez ajuster la taille des textes (<i class='fa fa-text-height' />), les positions horizontale (<i class='fa fa-arrows-alt-v' />) et verticale (<i class='fa fa-arrows-alt-h' />) du paysage.<br />
        </p>,
        <p>Si vous avez ajouté des logos partenaires, vous pouvez choisir de les afficher individuellement pour chacun des formats.</p>,
        <p>Cliquez sur un format pour le visualiser en mode plein écran.</p>
      ]
    ].filter(Boolean))
  },

  steps: {
    index: writable(0),
    length: writable(0),
    lastReached: undefined // See derivation below
  },

  regions: writable([
    {
      value: 'FFP Nationale',
      selected: true,
      signature: ['10, rue du Maréchal Joffre\n78000 Versailles\nFrance', customRegionContact()]
    },
    { value: 'FFP Île-de-France', signature: ['Île de France\n10, rue du Maréchal Joffre\n78000 Versailles', customRegionContact('idf@f-f-p.org')] },
    { value: 'FFP Normandie', signature: ['Normandie\nHameau de la Rivière\nRue Panorama\n14390 Petiville / Cabourg', customRegionContact('normandie@f-f-p.org')] },
    { value: 'FFP Hauts-de-France', signature: ['Hauts de France\nAtelier Altern\nTour C2 / appartement 181\n27, rue Lyautey\n59370 Mons en Baroeul', customRegionContact('hautsdefrance@f-f-p.org')] },
    { value: 'FFP Grand-Est, Champagne-Ardenne', signature: ['Grand-Est, Champagne-Ardenne\n30, avenue de la Gare \n10 800 Saint Julien les Villas', customRegionContact('champagneardenne@f-f-p.org')] },
    { value: 'FFP Grand-est, Alsace Lorraine', signature: ['Grand-Est, Alsace Lorraine\n39, rue de l’Ecole\n67330 Imbsheim-Bouxwiller', customRegionContact('alsace-lorraine@f-f-p.org')] },
    { value: 'FFP Auvergne-Rhône-Alpes, Bourgogne-Franche-Comté', signature: ['Auvergne-Rhône-Alpes, Bourgogne-Franche-Comté\n18, rue Ledru\n63000 Clermont-Ferrand', customRegionContact('aurabfc@f-f-p.org', '04 73 31 36 89')] },
    { value: 'FFP Provence, Alpes, Côte d’azur, Corse', signature: ['PACA Corse\nENSP / IMVT\n2, Place Jules Guesde\n13003 Marseille', customRegionContact('pacacorse@f-f-p.org')] },
    { value: 'FFP Occitanie, Languedoc-Roussillon', signature: ['Languedoc-Roussillon\nAgence Terrevive\n32, rue Bourrely\n34000 Montpellier', customRegionContact('languedocroussillon@f-f-p.org')] },
    { value: 'FFP Occitanie, Midi-Pyrénées', signature: ['Occitanie, Midi-Pyrénées\n33 route de la mouyssaguèse\n31130 Pin Balma', customRegionContact('midipyrenees@f-f-p.org')] },
    { value: 'FFP Nouvelle Aquitaine', signature: ['Nouvelle Aquitaine\n740, cours de la Libération\nBP 70109\n33405 Talence cedex', customRegionContact('nouvelleaquitaine@f-f-p.org')] },
    { value: 'FFP Bretagne, Pays de la Loire, Centre Val-de-Loire', signature: ['MATP (Maison de l’Architecture des Territoires et du Paysage)\n312 avenue René Gasnier\n49100 Angers', customRegionContact('grandouest@f-f-p.org')] },

    { disabled: true }, // This acts as a separator
    { value: 'Aucune signature' }
  ]),

  text: {
    h1: writable(''),
    h2: writable(''),
    h3: writable(''),
    h4: writable(''),
    h5: writable(''),
    h6: writable('')
  },

  landscape: {
    seed: writable(Date.now()),
    props: readable(window.ENV.landscape),
    colors: writable(undefined),
    abstraction: writable(undefined),
    isLoading: writable(false),
    image: writable(undefined)
  },

  logos: writable([]),
  cover: writable(undefined),

  formats: writable(window.ENV.formats || []),
  papers: writable(new Map()),

  poster: {
    template: readable(window.ENV.posterTemplate),
    fonts: readable({
      'Space Mono': { url: 'fonts/spacemono-regular.otf' },
      'Space Mono Bold': { url: 'fonts/spacemono-bold.otf' }
    }),
    // See derivation below
    hasNoLogos: undefined,
    hasNoTexts: undefined
  },

  APIServer: {
    url: readable('https://api.tooooools.com/'),
    public: readable('https://api.tooooools.com/'),
    version: writable(undefined),
    pollingInterval: readable(1000),
    pollingMaxAttempts: readable(30)
  }
}

// Signals derived from other Store signals:

Store.app.isRendering = derived(Store.app.papersBeingRendered, set => set.size > 0)
Store.steps.lastReached = derived(Store.steps.index, i => Store.steps.lastReached
  ? i > Store.steps.lastReached.current
    ? i
    : (Store.steps.lastReached.current || 0)
  : 0
)

Store.poster.hasNoLogos = derived(Store.logos, arr => !arr.length)
Store.poster.hasNoTexts = derived([Store.text.h1, Store.text.h2, Store.text.h3], () => {
  return !Store.text.h1.current && !Store.text.h2.current && !Store.text.h3.current
})

window.Store = Store
export default Store



// WEBPACK FOOTER //
// ./src/store/store.js